import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Loader2, Mail, Hash, AlertCircle } from 'lucide-react';
import api from '../utils/api';
import { setAuthToken, setUserName } from '../utils/auth';
import ThemeToggle from '../components/common/ThemeToggle';
import LanguageToggle from '../components/common/LanguageToggle';
import FlipCountdown from '../components/common/FlipCountdown';

const MAIN_PORTAL_URL = import.meta.env.VITE_MAIN_PORTAL_URL || 'http://localhost:3000';
const PREVIEW_KEY = 'aone_preview_unlocked';
// Launch date: Monday Jan 19, 2026 2:00 PM ET (19:00 UTC)
const LAUNCH_DATE = new Date('2026-01-19T19:00:00.000Z');

export default function Login() {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');

  // Pre-launch mode: auto-expires after launch date
  // To unlock early: type "admin" or set localStorage key manually
  const [isPreviewUnlocked, setIsPreviewUnlocked] = useState(() => {
    // Auto-unlock after launch date
    if (new Date() >= LAUNCH_DATE) return true;
    // Check localStorage for manual unlock
    if (localStorage.getItem(PREVIEW_KEY) === 'true') return true;
    return false;
  });

  // Auto-unlock when launch date passes
  useEffect(() => {
    if (isPreviewUnlocked) return;

    const checkExpiry = () => {
      if (new Date() >= LAUNCH_DATE) {
        setIsPreviewUnlocked(true);
      }
    };

    // Check every second
    const timer = setInterval(checkExpiry, 1000);
    return () => clearInterval(timer);
  }, [isPreviewUnlocked]);

  // Listen for "admin" keystroke sequence
  useEffect(() => {
    if (isPreviewUnlocked) return; // Already unlocked, no need to listen

    let buffer = '';
    const SECRET = 'admin';

    const handleKeyDown = (e) => {
      buffer += e.key.toLowerCase();
      // Keep only the last N characters (length of secret)
      if (buffer.length > SECRET.length) {
        buffer = buffer.slice(-SECRET.length);
      }
      // Check if buffer matches secret
      if (buffer === SECRET) {
        localStorage.setItem(PREVIEW_KEY, 'true');
        setIsPreviewUnlocked(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPreviewUnlocked]);
  const [formData, setFormData] = useState({
    email: '',
    consultation_number: '',
    remember_me: false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/parent-signin', formData);
      const { token, expiresIn, userName } = response.data;

      setAuthToken(token, expiresIn, formData.remember_me);
      setUserName(userName);
      navigate('/');
    } catch (err) {
      const serverMessage = err.response?.data?.message;
      const errorMap = {
        'Email not found': t('login.errors.emailNotFound'),
        'Consultation number does not match': t('login.errors.consultationMismatch'),
        'Email and consultation number are required': t('login.errors.missingFields'),
        'Cannot authenticate at the moment': t('login.errors.serverError'),
      };
      setError(errorMap[serverMessage] || t('login.errors.default'));
    } finally {
      setLoading(false);
    }
  };

  const isEmailActive = focusedField === 'email' || formData.email.length > 0;
  const isConsultationActive = focusedField === 'consultation_number' || formData.consultation_number.length > 0;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      {/* Header with Logo */}
      <header className="fixed top-0 left-0 right-0 z-100 backdrop-blur-md bg-white/70 dark:bg-gray-900/80 border-b border-[#E5E5E5] dark:border-gray-700 transition-colors">
        <div className="flex items-center justify-between px-4 py-3 sm:px-6 md:px-12 lg:px-20 sm:py-0">
          <div className="sm:pt-6 sm:pb-6 md:pt-7.75 md:pb-8.75">
            <a href="/" aria-label="home">
              {/* Light mode logo */}
              <img
                src="https://d1qf1106vb1ual.cloudfront.net/static/logo.svg"
                alt="Aone Institute"
                className="h-8 w-auto sm:h-8.5 sm:w-40 dark:hidden"
              />
              {/* Dark mode logo - white text with colored A */}
              <img
                src="/assets/logo-dark.svg"
                alt="Aone Institute"
                className="h-8 w-auto sm:h-8.5 sm:w-40 hidden dark:block"
              />
            </a>
          </div>
          <div className="flex items-center gap-1">
            <LanguageToggle />
            <ThemeToggle />
          </div>
        </div>
      </header>

      {/* Main Content - centered relative to full screen */}
      <main className="min-h-screen flex items-center justify-center px-4 sm:px-6 md:px-12 lg:px-20 py-8">
        <div className="max-w-md w-full animate-fade-in-up">
          {/* Coming Soon - shown when preview is not unlocked */}
          {!isPreviewUnlocked ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900/50 p-8 sm:p-12 transition-colors text-center">
              <div className="mb-6">
                <div className="w-16 h-16 mx-auto bg-[#589DD5]/10 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-[#589DD5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  {t('comingSoon.title')}
                </h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm sm:text-base mb-6">
                  {t('comingSoon.subtitle')}
                </p>
                <FlipCountdown
                  targetDate={LAUNCH_DATE}
                  labels={{
                    days: t('comingSoon.countdown.days'),
                    hours: t('comingSoon.countdown.hours'),
                    minutes: t('comingSoon.countdown.minutes'),
                    seconds: t('comingSoon.countdown.seconds'),
                  }}
                />
              </div>
              <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{t('login.teacherStudentPrompt')}</p>
                <a
                  href={MAIN_PORTAL_URL}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-[#589DD5] hover:text-[#4a8bc4] transition-colors group"
                >
                  {t('login.goToUniverse')}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </a>
              </div>
            </div>
          ) : (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900/50 p-6 sm:p-8 transition-colors">
            <div className="text-center mb-8">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{t('login.title')}</h1>
              <p className="text-gray-500 dark:text-gray-400 mt-2 text-sm sm:text-base">{t('login.subtitle')}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg text-sm flex items-start gap-3 animate-error-shake">
                  <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              {/* Email Input with Floating Label */}
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none">
                  <Mail className="w-5 h-5" />
                </div>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  required
                  placeholder=" "
                  className="peer w-full pl-11 pr-4 pt-5 pb-2 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:border-[#589DD5] focus:ring-4 focus:ring-[#589DD5]/20 transition-all"
                />
                <label
                  htmlFor="email"
                  className={`absolute left-11 transition-all duration-200 pointer-events-none ${
                    isEmailActive
                      ? 'top-2 text-xs text-[#589DD5]'
                      : 'top-1/2 -translate-y-1/2 text-sm text-gray-500 dark:text-gray-400'
                  }`}
                >
                  {t('login.emailLabel')}
                </label>
              </div>

              {/* Consultation Number Input with Floating Label */}
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none">
                  <Hash className="w-5 h-5" />
                </div>
                <input
                  type="text"
                  id="consultation_number"
                  name="consultation_number"
                  value={formData.consultation_number}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('consultation_number')}
                  onBlur={() => setFocusedField(null)}
                  required
                  placeholder=" "
                  className="peer w-full pl-11 pr-4 pt-5 pb-2 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:border-[#589DD5] focus:ring-4 focus:ring-[#589DD5]/20 transition-all"
                />
                <label
                  htmlFor="consultation_number"
                  className={`absolute left-11 transition-all duration-200 pointer-events-none ${
                    isConsultationActive
                      ? 'top-2 text-xs text-[#589DD5]'
                      : 'top-1/2 -translate-y-1/2 text-sm text-gray-500 dark:text-gray-400'
                  }`}
                >
                  {t('login.consultationLabel')}
                </label>
              </div>

              {/* Remember Me Checkbox with custom styling */}
              <div className="flex items-center">
                <label className="relative flex items-center cursor-pointer group">
                  <input
                    type="checkbox"
                    id="remember_me"
                    name="remember_me"
                    checked={formData.remember_me}
                    onChange={handleChange}
                    className="sr-only peer"
                  />
                  <div className="w-5 h-5 rounded border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 peer-checked:bg-[#589DD5] peer-checked:border-[#589DD5] transition-all flex items-center justify-center">
                    <svg
                      className={`w-3 h-3 text-white transition-opacity ${formData.remember_me ? 'opacity-100' : 'opacity-0'}`}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" className={formData.remember_me ? 'animate-checkmark' : ''} />
                    </svg>
                  </div>
                  <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">{t('login.rememberMe')}</span>
                </label>
              </div>

              {/* Submit Button with loading state */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#589DD5] text-white py-3 px-4 rounded-lg hover:bg-[#4a8bc4] focus:outline-none focus:ring-4 focus:ring-[#589DD5]/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium btn ripple-effect flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('login.signingIn')}
                  </>
                ) : (
                  t('login.signIn')
                )}
              </button>
            </form>

            {/* Below Form Portal Link */}
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{t('login.teacherStudentPrompt')}</p>
              <a
                href={MAIN_PORTAL_URL}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-[#589DD5] hover:text-[#4a8bc4] transition-colors group"
              >
                {t('login.goToUniverse')}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          </div>
          )}
        </div>
      </main>
    </div>
  );
}
